import { useTranslation } from 'react-i18next';
import DataTable, { type Column } from '@/components/base/DataTable';
import EmptyState from '@/components/base/EmptyState';
import ErrorState from '@/components/base/ErrorState';
import Skeleton from '@/components/base/Skeleton';
import { AccountDisabledBadge } from '@/pages/dashboard/drivers/components/DriverStatusBadges';
import type { DriverRatingRow } from '@/services/reviewsService';
import StarsRating from './StarsRating';

interface DriversRatingsPanelProps {
  rows: DriverRatingRow[] | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onView: (driverId: string) => void;
}

function DriverCell({ row }: { row: DriverRatingRow }) {
  const { driver } = row;
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-500 text-sm font-bold text-background-50">
        {driver.avatarUrl ? (
          <img src={driver.avatarUrl} alt={driver.fullName} className="h-full w-full object-cover" />
        ) : (
          driver.fullName.charAt(0)
        )}
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-foreground-950">{driver.fullName}</span>
          {driver.accountDisabled ? <AccountDisabledBadge /> : null}
        </span>
        <span className="lb-ltr-field mt-0.5 block text-xs text-foreground-400">{driver.id}</span>
      </span>
    </div>
  );
}

function RatingCell({ row }: { row: DriverRatingRow }) {
  const { t } = useTranslation();
  if (row.summary.average === null) {
    return <span className="text-sm text-foreground-400">{t('reviews.drivers.notRated')}</span>;
  }
  return (
    <div className="space-y-1">
      <StarsRating value={row.summary.average} size="md" showValue />
      <p className="text-xs text-foreground-500">
        {t('reviews.drivers.count')}: {row.summary.count}
      </p>
    </div>
  );
}

function ViewButton({
  driverId,
  onView,
}: {
  driverId: string;
  onView: (driverId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={() => onView(driverId)}
      className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
    >
      <i className="ri-eye-line text-base" aria-hidden="true" />
      {t('reviews.drivers.view')}
    </button>
  );
}

export default function DriversRatingsPanel({
  rows,
  loading,
  error,
  onRetry,
  onView,
}: DriversRatingsPanelProps) {
  const { t } = useTranslation();

  const columns: Column<DriverRatingRow>[] = [
    { key: 'name', header: t('reviews.drivers.col.name'), render: (row) => <DriverCell row={row} /> },
    { key: 'rating', header: t('reviews.drivers.col.rating'), render: (row) => <RatingCell row={row} /> },
    {
      key: 'actions',
      header: t('reviews.drivers.col.actions'),
      className: 'text-end',
      render: (row) => (
        <div className="flex justify-end">
          <ViewButton driverId={row.driver.id} onView={onView} />
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <ErrorState
        title={t('reviews.error.drivers')}
        description={t('common.errorText')}
        retryLabel={t('common.retry')}
        onRetry={onRetry}
      />
    );
  }

  if (loading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-4">
            <Skeleton className="h-11 w-11 rounded-full" />
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-5 flex-1" />
            <Skeleton className="h-8 w-28" />
          </div>
        ))}
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <EmptyState
        icon="ri-e-bike-2-line"
        title={t('reviews.drivers.empty')}
        description={t('reviews.drivers.emptyDesc')}
      />
    );
  }

  return (
    <>
      <div className="hidden md:block">
        <DataTable columns={columns} rows={rows} rowKey={(row) => row.driver.id} />
      </div>

      <ul className="divide-y divide-background-100 md:hidden">
        {rows.map((row) => (
          <li key={row.driver.id} className="space-y-3 p-4">
            <DriverCell row={row} />
            <div className="rounded-md border border-background-200 bg-background-100/50 p-3">
              <RatingCell row={row} />
            </div>
            <div className="border-t border-background-100 pt-3">
              <ViewButton driverId={row.driver.id} onView={onView} />
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}